<script setup lang="ts">
import type { ChangeValue } from "../sbi/protocol";
import type { TreeNode } from "../sbi/validate-render-op";

import InsightAccordion from "./InsightAccordion.vue";
import InsightCallout from "./InsightCallout.vue";
import InsightChart from "./InsightChart.vue";
import InsightCheckboxGroup from "./InsightCheckboxGroup.vue";
import InsightColumns from "./InsightColumns.vue";
import InsightDate from "./InsightDate.vue";
import InsightDateRange from "./InsightDateRange.vue";
import InsightDivider from "./InsightDivider.vue";
import InsightDrilldown from "./InsightDrilldown.vue";
import InsightIf from "./InsightIf.vue";
import InsightKpi from "./InsightKpi.vue";
import InsightMultiselect from "./InsightMultiselect.vue";
import InsightNumber from "./InsightNumber.vue";
import InsightNumberRange from "./InsightNumberRange.vue";
import InsightRelativeDate from "./InsightRelativeDate.vue";
import InsightSearch from "./InsightSearch.vue";
import InsightSection from "./InsightSection.vue";
import InsightSelect from "./InsightSelect.vue";
import InsightTable from "./InsightTable.vue";
import InsightDocuments from "./InsightDocuments.vue";
import InsightTabs from "./InsightTabs.vue";
import InsightText from "./InsightText.vue";
import InsightToggle from "./InsightToggle.vue";

withDefaults(
  defineProps<{
    node: TreeNode;
    loading?: boolean;
    /**
     * Field → format string registry produced by `sbi.format(...)`
     * during the latest render pass. Forwarded to every leaf so a
     * KPI / table column / chart axis can fall back to a registered
     * format when none was passed at the callsite. Defaults to `{}`
     * so the prop stays optional for ad-hoc embedding.
     */
    formats?: Record<string, string>;
  }>(),
  {
    formats: () => ({}),
  },
);

defineEmits<{
  (e: "click-target", id: string, datum?: Record<string, unknown>, rowIndex?: number): void;
  (e: "change-target", id: string, value: ChangeValue): void;
  /**
   * Chrome-click dispatch path — modal drilldown X / Esc / backdrop.
   * Bubbles up from `InsightDrilldown` through any container in
   * between to `EmbedHost`, which routes it to `runner.notifyClick`
   * WITHOUT applying the rerun-supersede gate. See
   * `InsightDrilldown.vue` for the rationale.
   */
  (e: "chrome-click-target", id: string): void;
  /** Forwarded from per-chart components — see InsightChart.vue. */
  (e: "diagnostic", code: string, detail: Record<string, unknown>): void;
  /**
   * Forwarded from InsightChart when a chart op carries an
   * `errorCode` from the 8-code taxonomy.
   */
  (
    e: "chart-error",
    payload: {
      code: string;
      message: string;
      chartType: string;
      targetId?: string;
    },
  ): void;
  /**
   * Forwarded from `InsightAccordion` on `<details>` toggle. Becomes
   * the `lucent-accordion-toggle` CustomEvent on `<lucent-insight>`.
   */
  (e: "accordion-toggle", summary: string, open: boolean): void;
  /**
   * Forwarded from `InsightCallout` on mount. Becomes the
   * `lucent-callout-rendered` CustomEvent on `<lucent-insight>` —
   * integrators consume for telemetry.
   */
  (e: "callout-rendered", kind: string, title?: string): void;
}>();
</script>

<template>
  <InsightKpi
    v-if="node.kind === 'kpi'"
    :label="node.label"
    :value="node.value"
    :opts="node.opts"
    :loading="node.loading ?? loading"
    :errored="node.errored"
    :ex-id="node.exId"
    :formats="formats"
    @click-target="(id) => $emit('click-target', id)"
  />
  <InsightText
    v-else-if="node.kind === 'text'"
    :markdown="node.markdown"
  />
  <InsightDocuments
    v-else-if="node.kind === 'documents'"
    :rows="node.rows"
    :opts="node.opts"
    :loading="node.loading ?? loading"
    :errored="node.errored"
    :ex-id="node.exId"
  />
  <InsightTable
    v-else-if="node.kind === 'table'"
    :rows="node.rows"
    :opts="node.opts"
    :loading="node.loading ?? loading"
    :errored="node.errored"
    :ex-id="node.exId"
    :formats="formats"
    :visible-actions-by-id="node.visibleActionsById"
    @click-target="(id, rowIndex) => $emit('click-target', id, undefined, rowIndex)"
  />
  <InsightChart
    v-else-if="node.kind === 'chart'"
    :spec="node.spec"
    :loading="node.loading ?? loading"
    :errored="node.errored"
    :error-code="node.kind === 'chart' ? node.errorCode : undefined"
    :ex-id="node.exId"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
    @diagnostic="(code, detail) => $emit('diagnostic', code, detail)"
    @chart-error="(payload) => $emit('chart-error', payload)"
  />
  <InsightSelect
    v-else-if="node.kind === 'select'"
    :id="node.id"
    :value="node.value"
    :options="node.options"
    :opts="node.opts"
    @change-target="(id, value) => $emit('change-target', id, value)"
  />
  <InsightMultiselect
    v-else-if="node.kind === 'multiselect'"
    :id="node.id"
    :value="node.value"
    :options="node.options"
    :opts="node.opts"
    @change-target="(id, value) => $emit('change-target', id, value)"
  />
  <InsightCheckboxGroup
    v-else-if="node.kind === 'checkbox_group'"
    :id="node.id"
    :value="node.value"
    :options="node.options"
    :opts="node.opts"
    @change-target="(id, value) => $emit('change-target', id, value)"
  />
  <InsightToggle
    v-else-if="node.kind === 'toggle'"
    :id="node.id"
    :value="node.value"
    :opts="node.opts"
    @change-target="(id, value) => $emit('change-target', id, value)"
  />
  <InsightDate
    v-else-if="node.kind === 'date'"
    :id="node.id"
    :value="node.value"
    :opts="node.opts"
    @change-target="(id, value) => $emit('change-target', id, value)"
  />
  <InsightDateRange
    v-else-if="node.kind === 'date_range'"
    :id="node.id"
    :value="node.value"
    :opts="node.opts"
    @change-target="(id, value) => $emit('change-target', id, value)"
  />
  <InsightNumber
    v-else-if="node.kind === 'number'"
    :id="node.id"
    :value="node.value"
    :opts="node.opts"
    @change-target="(id, value) => $emit('change-target', id, value)"
  />
  <InsightNumberRange
    v-else-if="node.kind === 'number_range'"
    :id="node.id"
    :value="node.value"
    :opts="node.opts"
    @change-target="(id, value) => $emit('change-target', id, value)"
  />
  <InsightSearch
    v-else-if="node.kind === 'search'"
    :id="node.id"
    :value="node.value"
    :opts="node.opts"
    @change-target="(id, value) => $emit('change-target', id, value)"
  />
  <InsightRelativeDate
    v-else-if="node.kind === 'relative_date'"
    :id="node.id"
    :value="node.value"
    :presets="node.presets"
    :opts="node.opts"
    @change-target="(id, value) => $emit('change-target', id, value)"
  />
  <InsightColumns
    v-else-if="node.kind === 'columns'"
    :total="node.total"
    :cells="node.cells"
    :match-height="node.matchHeight"
    :stack="node.stack"
    :loading="loading"
    :formats="formats"
    @click-target="(id, datum, rowIndex) => $emit('click-target', id, datum, rowIndex)"
    @change-target="(id, value) => $emit('change-target', id, value)"
    @chrome-click-target="(id) => $emit('chrome-click-target', id)"
    @accordion-toggle="(summary, open) => $emit('accordion-toggle', summary, open)"
    @callout-rendered="(kind, title) => $emit('callout-rendered', kind, title)"
  />
  <InsightTabs
    v-else-if="node.kind === 'tabs'"
    :names="node.names"
    :tabs="node.tabs"
    :icons="node.icons"
    :loading="loading"
    :formats="formats"
    @click-target="(id, datum, rowIndex) => $emit('click-target', id, datum, rowIndex)"
    @change-target="(id, value) => $emit('change-target', id, value)"
    @chrome-click-target="(id) => $emit('chrome-click-target', id)"
    @accordion-toggle="(summary, open) => $emit('accordion-toggle', summary, open)"
    @callout-rendered="(kind, title) => $emit('callout-rendered', kind, title)"
  />
  <InsightIf
    v-else-if="node.kind === 'if'"
    :node="node"
    :loading="loading"
    :formats="formats"
    @click-target="(id, datum, rowIndex) => $emit('click-target', id, datum, rowIndex)"
    @change-target="(id, value) => $emit('change-target', id, value)"
    @chrome-click-target="(id) => $emit('chrome-click-target', id)"
    @accordion-toggle="(summary, open) => $emit('accordion-toggle', summary, open)"
    @callout-rendered="(kind, title) => $emit('callout-rendered', kind, title)"
  />
  <InsightDrilldown
    v-else-if="node.kind === 'drilldown'"
    :node="node"
    :loading="loading"
    :formats="formats"
    @click-target="(id, datum, rowIndex) => $emit('click-target', id, datum, rowIndex)"
    @change-target="(id, value) => $emit('change-target', id, value)"
    @chrome-click-target="(id) => $emit('chrome-click-target', id)"
    @accordion-toggle="(summary, open) => $emit('accordion-toggle', summary, open)"
    @callout-rendered="(kind, title) => $emit('callout-rendered', kind, title)"
  />
  <InsightCallout
    v-else-if="node.kind === 'callout'"
    :callout-kind="node.calloutKind"
    :title="node.title"
    :icon="node.icon"
    :children="node.children"
    :loading="loading"
    :formats="formats"
    @click-target="(id, datum, rowIndex) => $emit('click-target', id, datum, rowIndex)"
    @change-target="(id, value) => $emit('change-target', id, value)"
    @chrome-click-target="(id) => $emit('chrome-click-target', id)"
    @accordion-toggle="(summary, open) => $emit('accordion-toggle', summary, open)"
    @callout-rendered="(kind, title) => $emit('callout-rendered', kind, title)"
  />
  <InsightDivider v-else-if="node.kind === 'divider'" />
  <InsightSection
    v-else-if="node.kind === 'section'"
    :title="node.title"
    :caption="node.caption"
    :description="node.description"
    :icon="node.icon"
    :icon-position="node.iconPosition"
    :children="node.children"
    :loading="loading"
    :formats="formats"
    @click-target="(id, datum, rowIndex) => $emit('click-target', id, datum, rowIndex)"
    @change-target="(id, value) => $emit('change-target', id, value)"
    @chrome-click-target="(id) => $emit('chrome-click-target', id)"
    @accordion-toggle="(summary, open) => $emit('accordion-toggle', summary, open)"
    @callout-rendered="(kind, title) => $emit('callout-rendered', kind, title)"
  />
  <InsightAccordion
    v-else-if="node.kind === 'accordion'"
    :summary="node.summary"
    :default-open="node.defaultOpen"
    :icon="node.icon"
    :icon-position="node.iconPosition"
    :children="node.children"
    :loading="loading"
    :formats="formats"
    @click-target="(id, datum, rowIndex) => $emit('click-target', id, datum, rowIndex)"
    @change-target="(id, value) => $emit('change-target', id, value)"
    @chrome-click-target="(id) => $emit('chrome-click-target', id)"
    @accordion-toggle="(summary, open) => $emit('accordion-toggle', summary, open)"
    @callout-rendered="(kind, title) => $emit('callout-rendered', kind, title)"
  />
</template>
